import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { convertLegacyCoordinates, USE_NAVIGATOR_V2 } from '../config/mapConfig';
import { resolveStatus } from '../utils/statusResolver';
import { getInvestmentPropertiesAtDate, getAdditionalCostsAtDate } from '@repo/supabase';
import { PropertyType, PriceBadge, PricePerSquareMeter } from '@repo/domain';

// Helper function to fetch related investment slugs from database
const fetchRelatedInvestmentSlugs = async (investmentSlug) => {
  const { data, error } = await supabase
    .from('investments')
    .select(`
      id,
      relations:investment_relations!investment_id(
        related:investments!related_investment_id(slug)
      )
    `)
    .eq('slug', investmentSlug)
    .maybeSingle();

  if (error || !data) {
    console.warn('Could not fetch related investments:', error);
    return [];
  }

  return data.relations?.map(r => r.related?.slug).filter(Boolean) || [];
};

// Helper function to fetch properties for a single investment slug
const fetchPropertiesForSlug = async (slug, currentDate) => {
  const data = await getInvestmentPropertiesAtDate(
    supabase,
    slug,
    new Date(currentDate)
  );
  return data;
};

// Helper function to resolve floor label from floor number
const resolveFloorLabel = (floorNumber, hasStorefront) => {
  if (floorNumber === null || floorNumber === undefined) return '';
  if (floorNumber === 0) return hasStorefront ? 'Parter z witrynami' : 'Parter';
  return `${floorNumber} piętro`;
};

export const useProperties = (investmentSlug = 'warszawska-33') => {
  const [properties, setProperties] = useState([]);
  const [optionalUnits, setOptionalUnits] = useState([]);
  const [allPropertiesForMap, setAllPropertiesForMap] = useState([]);
  const [investmentData, setInvestmentData] = useState(null);
  const [footerConfig, setFooterConfig] = useState(null);
  const [globalConfig, setGlobalConfig] = useState(null);
  const [isMultiInvestment, setIsMultiInvestment] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        setLoading(true);
        
        // Fetch investment data
        const { data: investmentInfo, error: investmentError } = await supabase
          .from('investments')
          .select(`
            *,
            tenant:tenants!inner(
              id,
              name,
              slug,
              investor_name,
              investor_address,
              investor_email,
              investor_phone,
              investor_website,
              investor_nip,
              investor_krs,
              investor_logo_url,
              hq_street,
              hq_building_number,
              hq_postal_code,
              hq_city,
              has_module_price_badge
            )
          `)
          .eq('slug', investmentSlug)
          .maybeSingle();

        if (investmentError) {
          console.error('Investment error:', investmentError);
          throw investmentError;
        }

        if (!investmentInfo) {
          // No investment found - set error but continue loading global config
          setError('Nie znaleziono inwestycji');
          setInvestmentData(null);
          
          // Still fetch global config for fallback email
          const { data: globalConfigData } = await supabase
            .from('global_configs')
            .select('*');
          
          const configObject = {};
          globalConfigData?.forEach(item => {
            configObject[item.key] = item.value;
          });
          setGlobalConfig(configObject);
          
          setLoading(false);
          return; // Exit early
        }

        // Add map configuration based on navigator_image_url from database
        // When USE_NAVIGATOR_V2 is enabled, use navigator_config_v2 if available
        let mapConfiguration = null;
        let navigatorConfigV2 = null;

        if (USE_NAVIGATOR_V2 && investmentInfo.navigator_config_v2) {
          // V2 format: use artboard data from navigator_config_v2
          navigatorConfigV2 = investmentInfo.navigator_config_v2;
          const artboard = navigatorConfigV2.artboards?.[0];
          if (artboard) {
            mapConfiguration = {
              imageUrl: artboard.image?.url,
              imageAlt: `Plan inwestycji ${investmentInfo.name}`,
              viewBox: artboard.viewBox,
              aspectRatio: artboard.image?.width / artboard.image?.height,
              dimensions: {
                width: artboard.image?.width,
                height: artboard.image?.height
              }
            };
          }
        } else if (investmentInfo.navigator_image_url) {
          // V1 format: use navigator_image_url
          mapConfiguration = {
            imageUrl: investmentInfo.navigator_image_url,
            imageAlt: `Plan inwestycji ${investmentInfo.name}`,
            viewBox: '0 0 100 56.5',
            aspectRatio: 1.77,
            dimensions: {
              width: 100,
              height: 56.5
            }
          };
        }

        const investmentWithMapConfig = {
          ...investmentInfo,
          hideFilterControls: investmentInfo.hide_filter_controls || false,
          enableNavigatorTooltips: investmentInfo.enable_navigator_tooltips || false,
          navigatorClickOpensCard: investmentInfo.is_navigator_click_opens_card || false,
          mapConfiguration,
          navigatorConfigV2
        };

        setInvestmentData(investmentWithMapConfig);
        setFooterConfig(null); // No longer needed

        // Fetch global config
        const { data: globalConfigData, error: globalConfigError } = await supabase
          .from('global_configs')
          .select('*');

        if (globalConfigError) {
          console.error('Global config error:', globalConfigError);
        } else {
          // Convert key-value pairs to object
          const configObject = {};
          globalConfigData?.forEach(item => {
            configObject[item.key] = item.value;
          });
          setGlobalConfig(configObject);
        }

        // Use versioning function to get properties with current prices and statuses
        const currentDate = new Date().toISOString();

        // Fetch properties from main investment
        const mainPropertiesData = await fetchPropertiesForSlug(investmentSlug, currentDate);

        // Check for related investments and fetch their properties
        const relatedSlugs = await fetchRelatedInvestmentSlugs(investmentSlug);
        const hasRelatedInvestments = relatedSlugs.length > 0;
        setIsMultiInvestment(hasRelatedInvestments);
        let propertiesData = [...mainPropertiesData];

        if (hasRelatedInvestments) {
          console.log('Fetching properties from related investments:', relatedSlugs);

          // Fetch properties from each related investment in parallel
          const relatedPropertiesPromises = relatedSlugs.map(slug =>
            fetchPropertiesForSlug(slug, currentDate)
          );

          const relatedPropertiesResults = await Promise.all(relatedPropertiesPromises);

          // Union all properties
          relatedPropertiesResults.forEach((properties, index) => {
            console.log(`Related investment ${relatedSlugs[index]}:`, properties.length, 'properties');
            propertiesData = [...propertiesData, ...properties];
          });
        }

        console.log('Properties fetched from versioning system:', propertiesData?.length || 0, 'properties');

        // Fetch additional data that's not included in the versioning function
        const propertyIds = propertiesData.map(p => p.id);
        
        // Get property type info, media, and stage information
        const { data: propertyDetailsData, error: detailsError } = await supabase
          .from('properties')
          .select(`
            id,
            external_id,
            optional_unit_postfix,
            floor_levels,
            property_type_info:property_types!properties_property_type_fkey(
              key,
              label
            ),
            main_image_media:property_medias!main_image_id(
              id,
              file_url,
              alt_text
            ),
            card_image_media:property_medias!card_image_id(
              id,
              file_url,
              alt_text
            ),
            image_2d_media:property_medias!image_2d_id(
              id,
              file_url,
              alt_text
            ),
            main_image,
            agent_phone,
            agent_email,
            agent_whatsapp,
            living_area_value,
            living_area_unit,
            parking_spots,
            garage_spots,
            parking_price,
            garage_price,
            parking_included_in_price,
            garage_included_in_price,
            map_coordinates,
            area_unit,
            rooms_total_extra_variant,
            rooms_label,
            garden_area,
            plot_area,
            terrace_area,
            terraces_details,
            has_gastro_potential,
            has_plot,
            sales_start_date,
            exposure,
            has_storefront,
            parking_group,
            use_single_column_layout,
            is_price_badge_hidden,
            stage:stages!inner(
              id,
              name,
              prospectus_url,
              investment:investments!inner(
                id,
                name,
                slug,
                prospectus_url,
                privacy_policy_url,
                contact_url,
                tenant:tenants!inner(
                  id,
                  name,
                  slug,
                  investor_name,
                  investor_address,
                  investor_email,
                  investor_phone,
                  investor_website,
                  investor_nip,
                  investor_krs,
                  investor_logo_url,
                  hq_street,
                  hq_building_number,
                  hq_postal_code,
                  hq_city
                )
              )
            )
          `)
          .in('id', propertyIds);

        if (detailsError) throw detailsError;

        // Get additional costs using versioning function
        const additionalCostsData = await getAdditionalCostsAtDate(
          supabase,
          propertyIds,
          new Date(currentDate)
        );

        if (!additionalCostsData) {
          console.warn('Error fetching additional costs:', costsError);
        }

        // Get price history from property_price_versions
        const { data: priceHistoryData, error: historyError } = await supabase
          .from('property_price_versions')
          .select('*')
          .in('property_id', propertyIds)
          .order('valid_from', { ascending: false });

        if (historyError) {
          console.warn('Error fetching price history:', historyError);
        }

        // Get additional features from junction table (eco/smart-home features)
        const { data: propertyFeaturesData, error: featuresError } = await supabase
          .from('property_features')
          .select(`
            property_id,
            feature:additional_features!inner(
              key,
              label,
              icon_svg
            )
          `)
          .in('property_id', propertyIds);

        if (featuresError) {
          console.warn('Error fetching property features:', featuresError);
        }

        // Fetch all additional_features for legacy feature icon lookup
        const { data: allFeaturesData, error: allFeaturesError } = await supabase
          .from('additional_features')
          .select('key, label, icon_svg');

        if (allFeaturesError) {
          console.warn('Error fetching all features:', allFeaturesError);
        }

        // Create lookup map for feature icons
        const featureIconMap = {};
        (allFeaturesData || []).forEach(f => {
          featureIconMap[f.key] = { label: f.label, icon_svg: f.icon_svg };
        });

        // Group property features by property_id
        const propertyFeaturesByProperty = {};
        (propertyFeaturesData || []).forEach(pf => {
          if (!propertyFeaturesByProperty[pf.property_id]) {
            propertyFeaturesByProperty[pf.property_id] = [];
          }
          propertyFeaturesByProperty[pf.property_id].push({
            key: pf.feature.key,
            label: pf.feature.label,
            icon_svg: pf.feature.icon_svg
          });
        });

        // Group additional costs by property
        const additionalCostsByProperty = {};
        (additionalCostsData || []).forEach(cost => {
          if (!additionalCostsByProperty[cost.property_id]) {
            additionalCostsByProperty[cost.property_id] = [];
          }
          additionalCostsByProperty[cost.property_id].push(cost);
        });

        // Group price history by property and format
        const priceHistoryByProperty = {};
        (priceHistoryData || []).forEach(history => {
          if (!priceHistoryByProperty[history.property_id]) {
            priceHistoryByProperty[history.property_id] = [];
          }
          priceHistoryByProperty[history.property_id].push({
            date: history.valid_from,
            totalPrice: {
              value: parseFloat(history.total_price),
              currency: history.currency
            },
            pricePerSquareMeter: {
              value: parseFloat(history.total_price), // Will be calculated below
              currency: history.currency
            }
          });
        });

        // Create a map of property details for easy lookup
        const propertyDetailsMap = {};
        propertyDetailsData.forEach(detail => {
          propertyDetailsMap[detail.id] = detail;
        });

        const hasPriceBadgeModule = investmentInfo.tenant?.has_module_price_badge || false;

        // Transform data to match original JSON structure
        const transformedProperties = propertiesData.map((property) => {
          const details = propertyDetailsMap[property.id] || {};

          // Calculate price per sqm for history entries
          const historyWithPricePerSqm = (priceHistoryByProperty[property.id] || []).map(entry => ({
            ...entry,
            pricePerSquareMeter: new PricePerSquareMeter(
              property.area_total > 0 ? entry.totalPrice.value / property.area_total : 0,
              entry.totalPrice.currency
            )
          }));

          const priceHistory = priceHistoryByProperty[property.id] || [];
          const maxHistoricalPrice = priceHistory.length > 0
            ? Math.max(...priceHistory.map(h => h.totalPrice.value))
            : null;

          const priceBadge = new PriceBadge({
            isHidden: !hasPriceBadgeModule || (details.is_price_badge_hidden ?? false),
            currentPrice: parseFloat(property.total_price),
            maxHistoricalPrice,
            label: property.promo_badge_label,
            backgroundColor: property.promo_badge_background_color,
            textColor: property.promo_badge_text_color,
          });
          
          return {
            id: property.external_id,
            title: property.title,
            propertyType: property.property_type,
            propertyTypeLabel: details.property_type_info?.label || property.property_type,
            optionalUnitPostfix: details.optional_unit_postfix,
            status: resolveStatus(property.status_code),
            stage: {
              id: details.stage?.id,
              name: details.stage?.name,
              prospectus_url: details.stage?.prospectus_url
            },
            location: {
              floor_label: resolveFloorLabel(property.floor_number, details.has_storefront),
              floor: property.floor_number
            },
            specifications: {
              area: {
                total: parseFloat(property.area_total),
                unit: details.area_unit
              },
              rooms: {
                total: property.rooms_total,
                totalExtraVariant: details.rooms_total_extra_variant,
                label: details.rooms_label
              },
              features: (() => {
                // Build unified features.all array
                const allFeatures = [];

                // Legacy features (boolean columns)
                if (property.has_balcony) {
                  const f = featureIconMap['balcony'] || {};
                  allFeatures.push({ key: 'balcony', label: f.label || 'Balkon', icon_svg: f.icon_svg, available: true });
                }
                if (property.has_terrace) {
                  const f = featureIconMap['terrace'] || {};
                  allFeatures.push({ key: 'terrace', label: f.label || 'Taras', icon_svg: f.icon_svg, available: true, area: parseFloat(details.terrace_area) });
                }
                if (property.has_garden) {
                  const f = featureIconMap['garden'] || {};
                  allFeatures.push({ key: 'garden', label: f.label || 'Ogród', icon_svg: f.icon_svg, available: true, area: parseFloat(details.garden_area) });
                }
                if (property.has_parking) {
                  const f = featureIconMap['parking'] || {};
                  allFeatures.push({ key: 'parking', label: f.label || 'Miejsce parkingowe', icon_svg: f.icon_svg, available: true });
                }
                if (property.has_garage) {
                  const f = featureIconMap['garage'] || {};
                  allFeatures.push({ key: 'garage', label: f.label || 'Garaż', icon_svg: f.icon_svg, available: true });
                }
                if (property.has_ventilation) {
                  const f = featureIconMap['ventilation'] || {};
                  allFeatures.push({ key: 'ventilation', label: f.label || 'Wentylacja mechaniczna', icon_svg: f.icon_svg, available: true });
                }

                // New features from junction table (eco/smart-home)
                const extraFeatures = propertyFeaturesByProperty[property.id] || [];
                extraFeatures.forEach(ef => {
                  allFeatures.push({ key: ef.key, label: ef.label, icon_svg: ef.icon_svg });
                });

                return {
                  // Legacy fields (for backward compatibility)
                  balcony: property.has_balcony,
                  terrace: {
                    available: property.has_terrace,
                    area: parseFloat(details.terrace_area),
                    hasGastroPotential: details.has_gastro_potential,
                    details: details.terraces_details || null
                  },
                  garden: {
                    available: property.has_garden,
                    area: parseFloat(details.garden_area)
                  },
                  plot: {
                    available: details.has_plot,
                    area: parseFloat(details.plot_area)
                  },
                  parking: property.has_parking,
                  garage: property.has_garage,
                  ventilation: property.has_ventilation,
                  // Unified array for UI rendering
                  all: allFeatures
                };
              })()
            },
            pricing: {
              totalPrice: {
                value: parseFloat(property.total_price),
                currency: property.currency
              },
              pricePerSquareMeter: new PricePerSquareMeter(
                parseFloat(property.price_per_sqm),
                property.currency
              ),
              salesStartDate: details.sales_start_date,
              priceHistory: historyWithPricePerSqm,
              additionalCosts: additionalCostsByProperty[property.id] || []
            },
            media: {
              main_image: details.main_image_media?.file_url || details.main_image,
              card_image: details.card_image_media?.file_url,
              image_2d: details.image_2d_media?.file_url,
              main_image_alt: details.main_image_media?.alt_text,
              card_image_alt: details.card_image_media?.alt_text,
              image_2d_alt: details.image_2d_media?.alt_text
            },
            contact: {
              agent_phone: details.agent_phone,
              agent_email: details.agent_email,
              agent_whatsapp: details.agent_whatsapp
            },
            investmentUrls: {
              prospectus_url: details.stage?.investment?.prospectus_url,
              privacy_policy_url: details.stage?.investment?.privacy_policy_url,
              contact_url: details.stage?.investment?.contact_url
            },
            identifier: property.external_id,
            livingArea: {
              value: parseFloat(details.living_area_value),
              unit: details.living_area_unit
            },
            parkingSpots: details.parking_spots,
            garageSpots: details.garage_spots,
            parkingPrice: details.parking_price ? parseFloat(details.parking_price) : null,
            garagePrice: details.garage_price ? parseFloat(details.garage_price) : null,
            parkingIncludedInPrice: details.parking_included_in_price,
            garageIncludedInPrice: details.garage_included_in_price,
            // Use map coordinates from database, convert from legacy viewBox if needed
            mapCoordinates: details.map_coordinates
              ? convertLegacyCoordinates(details.map_coordinates)
              : null,
            exposure: details.exposure,
            floor_levels: details.floor_levels,
            // Parking group for filtering optional units
            parkingGroup: details.parking_group,
            // Layout flag for property card display
            useSingleColumnLayout: details.use_single_column_layout || false,
            priceBadge,
            // Tenant data from property's investment (for multi-investment mode)
            tenant: details.stage?.investment?.tenant
          };
        });

        // Sort properties by title first (natural order for numbers), then by status priority: available -> reserved -> sold
        const statusPriority = {
          'available': 1,
          'reserved': 2,
          'sold': 3
        };
        
        const sortedProperties = transformedProperties.sort((a, b) => {
          // Primary sorting: natural sort by title (handles numbers properly)
          const titleA = a.title.toLowerCase();
          const titleB = b.title.toLowerCase();
          const titleComparison = titleA.localeCompare(titleB, 'pl', { numeric: true });
          
          if (titleComparison !== 0) {
            return titleComparison;
          }
          
          // Secondary sorting: by status if titles are identical
          const priorityA = statusPriority[a.status.code];
          const priorityB = statusPriority[b.status.code];
          return priorityA - priorityB;
        });

        // Separate main properties from optional units
        const OPTIONAL_UNIT_TYPES = [
          PropertyType.MiejscePostojowe,
          PropertyType.Komorka,
          PropertyType.Garaz,
          PropertyType.BoksGarazowy,
        ];

        const mainProperties = sortedProperties.filter(property => {
          return !OPTIONAL_UNIT_TYPES.includes(property.propertyType);
        });

        const optionalUnits = sortedProperties.filter(property => {
          return OPTIONAL_UNIT_TYPES.includes(property.propertyType)
            && property.status.code === 'available';
        });

        // Extract storage units (komórki) from additional costs for map coloring
        // They have identifiers like "Komórka lokatorska 01" which maps to navigator's "KL01"
        const storageUnitsForMap = [];
        const storageTypes = ['komorka', 'storage'];

        (additionalCostsData || []).forEach(cost => {
          if (storageTypes.includes(cost.cost_type)) {
            // Extract number from description like "Komórka lokatorska 01 (3,99 m²)"
            // Match pattern: "Komórka lokatorska XX" where XX is the storage number
            const match = cost.description?.match(/[Kk]om[óo]rka\s+(?:lokatorska\s+)?(\d+)/i);
            if (match) {
              // Keep original number format (with leading zeros) to match navigator IDs
              const storageNumber = match[1];
              // Create identifier matching navigator format (KL01, KL02, etc.)
              const storageId = `KL${storageNumber}`;

              // Extract area from description like "(3,99 m²)"
              const areaMatch = cost.description?.match(/\((\d+[,.]?\d*)\s*m²?\)/i);
              const areaValue = areaMatch ? parseFloat(areaMatch[1].replace(',', '.')) : null;

              // Find parent property to inherit status
              const parentProperty = transformedProperties.find(p =>
                additionalCostsByProperty[propertiesData.find(pd => pd.external_id === p.id)?.id]?.some(ac => ac.id === cost.id)
              );

              // Get parent property status (if parent is sold/reserved, storage is too)
              const parentStatus = parentProperty?.status || { code: 'available', label: 'Dostępny', color: '#4CAF50' };

              storageUnitsForMap.push({
                id: storageId,
                title: `Komórka lokatorska ${storageNumber}`,
                propertyType: 'komorka-lokatorska',
                propertyTypeLabel: 'Komórka lokatorska',
                status: parentStatus,
                isStorageUnit: true,
                isTooltipOnly: true, // Flag to show tooltip instead of list selection
                specifications: {
                  area: {
                    total: areaValue,
                    unit: 'm²'
                  }
                },
                pricing: {
                  totalPrice: {
                    value: parseFloat(cost.amount) || 0,
                    currency: 'PLN'
                  }
                }
              });
            }
          }
        });

        console.log('Storage units for map:', storageUnitsForMap.length);

        // All properties for map coloring (includes ALL optional units regardless of status + storage units)
        const allPropertiesForMap = [...sortedProperties, ...storageUnitsForMap];

        // Apply hide_unavailable_properties filter if enabled
        let filteredMainProperties = mainProperties;
        if (investmentWithMapConfig.hide_unavailable_properties) {
          const beforeCount = filteredMainProperties.length;
          filteredMainProperties = filteredMainProperties.filter(property => {
            return property.status.code === 'available';
          });
          console.log('Hide unavailable properties enabled:', beforeCount, '→', filteredMainProperties.length, 'properties');
        }

        console.log('Properties filtered:', sortedProperties.length, '→', filteredMainProperties.length, '(excluded optional units)');
        console.log('Optional units available:', optionalUnits.length);

        setProperties(filteredMainProperties);
        setOptionalUnits(optionalUnits);
        setAllPropertiesForMap(allPropertiesForMap);
      } catch (err) {
        console.error('Error fetching properties:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [investmentSlug]);

  return { properties, optionalUnits, allPropertiesForMap, investmentData, footerConfig, globalConfig, isMultiInvestment, loading, error };
};